'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Sparkles, FileCode, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { xmlToJsonContent } from '../../data/xml-to-json-content';

const sampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<person>
  <name>John Doe</name>
  <age>30</age>
  <isActive>true</isActive>
  <address>
    <street>123 Main St</street>
    <city>New York</city>
    <country>USA</country>
  </address>
  <emails>
    <email>john@example.com</email>
    <email>johndoe@work.com</email>
  </emails>
  <phoneNumbers>
    <phone type="home">555-1234</phone>
    <phone type="work">555-5678</phone>
  </phoneNumbers>
</person>`;

const sampleJson = `{
  "person": {
    "name": "John Doe",
    "age": 30,
    "isActive": true,
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "country": "USA"
    },
    "emails": {
      "email": [
        "john@example.com",
        "johndoe@work.com"
      ]
    },
    "phoneNumbers": {
      "phone": [
        { "type": "home", "value": "555-1234" },
        { "type": "work", "value": "555-5678" }
      ]
    }
  }
}`;

function formatJson(json: string): string {
    try {
        const obj = JSON.parse(json);
        return JSON.stringify(obj, null, 2);
    } catch (e) {
        return json;
    }
}

function xmlToJson(xmlString: string): string {
    try {
        // Parse XML string to DOM
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

        // Check for XML parsing errors
        const parserError = xmlDoc.getElementsByTagName('parsererror');
        if (parserError.length > 0) {
            throw new Error('Invalid XML format');
        }

        // Convert XML to JavaScript object
        const result = xmlNodeToJson(xmlDoc.documentElement);

        // Convert to JSON string with proper formatting
        return JSON.stringify(result, null, 2);
    } catch (error) {
        throw new Error(`Error converting XML to JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

function xmlNodeToJson(node: Element | ChildNode): any {
    // Handle text nodes
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue?.trim() || '';
        return text === '' ? null : text;
    }

    // Handle element nodes
    if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const obj: Record<string, any> = {};

        // Process attributes
        if (element.attributes.length > 0) {
            obj['@attributes'] = {};
            for (let i = 0; i < element.attributes.length; i++) {
                const attr = element.attributes[i];
                obj['@attributes'][attr.name] = attr.value;
            }
        }

        // Process child nodes
        const children = Array.from(element.childNodes);

        // If no children, return empty object or text content
        if (children.length === 0) {
            return Object.keys(obj).length > 0 ? obj : '';
        }

        // Process all child nodes
        for (const child of children) {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.nodeValue?.trim() || '';
                if (text) {
                    obj['#text'] = text;
                }
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                const childElement = child as Element;
                const childName = childElement.tagName;
                const childObj = xmlNodeToJson(childElement);

                // Handle arrays (multiple elements with same name)
                if (obj[childName] === undefined) {
                    obj[childName] = childObj;
                } else {
                    if (!Array.isArray(obj[childName])) {
                        obj[childName] = [obj[childName]];
                    }
                    obj[childName].push(childObj);
                }
            }
        }

        // If only has text content and no attributes, return the text directly
        if (Object.keys(obj).length === 1 && obj['#text'] !== undefined) {
            return obj['#text'];
        }

        return obj;
    }

    return null;
}

export default function XmlToJsonTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isPretty, setIsPretty] = useState(true);
    const { toast } = useToast();

    const convertXmlToJson = (xmlString: string) => {
        try {
            if (!xmlString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            const jsonString = xmlToJson(xmlString);
            setOutput(jsonString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid XML'}`);
            setOutput('');
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            convertXmlToJson(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input]);

    useEffect(() => {
        if (output && isPretty) {
            setOutput(formatJson(output));
        }
    }, [isPretty]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The JSON has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
    };

    const loadSample = () => {
        setInput(sampleXml);
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="XML to JSON Converter - Free Online Data Format Converter">
                Convert XML data to JSON format instantly with our free online converter. Perfect for developers, data analysts, and API integrations.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        XML Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Paste your XML data here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your XML here...\n<example>\n  <data>value</data>\n  <items>\n    <item>1</item>\n    <item>2</item>\n  </items>\n</example>'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        JSON Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Your converted JSON will appear here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsPretty(!isPretty)}
                                        className={cn(
                                            'gap-1.5',
                                            isPretty ? 'bg-primary/5' : ''
                                        )}
                                    >
                                        {isPretty ? (
                                            <FileText className="h-3.5 w-3.5" />
                                        ) : (
                                            <FileCode className="h-3.5 w-3.5" />
                                        )}
                                        {isPretty ? 'Pretty' : 'Minified'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70'
                                    )}
                                    placeholder="Your converted JSON will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your converted JSON will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={xmlToJsonContent.title}
                    description={xmlToJsonContent.description}
                    content={xmlToJsonContent.content}
                    faqs={xmlToJsonContent.faqs}
                    relatedTools={xmlToJsonContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
